import React, { useRef, useEffect, forwardRef, useState } from 'react';
import '../../shared/columns/styles/PopupWindowInput.scss';
import {
    KEY_BACKSPACE,
    KEY_TAB,
    KEY_ENTER,
    KEY_LEFT,
    KEY_RIGHT,
    KEY_PLUS,
    KEY_FORWARD_SLASH,
    KEY_EQUAL,
} from '../../../constants/keyboardCodes';
import {
    SEPARATOR_PLUS,
    SEPARATOR_FORWARD_SLASH,
} from '../../../models/common/EntityPart';
import { changeFocusOnKeyDown } from '_legacy/services/TrapFocusService';
import { createSeparatorTcePart, createTcePart } from './TcePart';
import Select from 'react-select';
import LocationSelect from '_legacy/components/grid/inputs/LocationSelect';
import _ from 'lodash';
import { balticOptions } from './formatters/TceFormatter';
import { QuantityParserInstance } from '_legacy/modules/shared/columns/tools/quantityParser';
import { selectStyles } from '../rate/inputs/RateIndex';

interface Location {
    country: string;
    deskArea: string;
    deskAreaGeared: string;
    deskZone: string;
    displayName: string;
    globalZone: string;
    id: string;
    type: string;
    name: string;
    value: string;
    isSTS: boolean;
}

export interface LocationPartInput {
    name: string;
    value: string;
    partType: string;
    isSTS: boolean;
}

interface Tce {
    rate: string;
    ballastBonus?: string | null;
    balticIndex?: string | null;
    delivery?: LocationPartInput | null;
    redelivery?: LocationPartInput | null;
}

const initialTceState: Tce = {
    rate: '',
    ballastBonus: null,
    balticIndex: null,
    delivery: null,
    redelivery: null,
};

const TceInput = forwardRef((props: any, ref) => {
    const containerId = 'tce-elements';

    const didMountRef = useRef(false);
    const rateInputRef = useRef<HTMLInputElement>(null);
    const ballastBonusInputRef = useRef<HTMLInputElement>(null);
    const deliveryInputRef = useRef<any>(null);
    const redeliveryInputRef = useRef<any>(null);

    const [tce, setTce] = useState<Tce>(initialTceState);
    const [selectedIndex, setSelectedIndex] = useState<number | undefined>(
        undefined
    );
    const [rateValid, setRateValid] = useState<boolean>(true);
    const [ballastBonusValid, setBallastBonusValid] = useState<boolean>(true);
    const [deliveryValid, setDeliveryValid] = useState<boolean>(true);
    const [redeliveryValid, setRedeliveryValid] = useState<boolean>(true);

    useEffect(() => {
        if (didMountRef.current) {
            focusRate();
        } else {
            didMountRef.current = true;
            focusRate();
        }
    }, []);

    useEffect(() => {
        if (props.isEditMode && props.selectedIndex !== selectedIndex) {
            setSelectedIndex(props.selectedIndex);
            setTce(props.selectedValue.tceValue);
        } else {
            reset();
            setSelectedIndex(undefined);
            focusRate();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isEditMode, props.selectedIndex]);

    const reset = () => {
        setTce(initialTceState);
    };

    const focusRate = () => {
        if (rateInputRef.current) {
            setTimeout(() => {
                rateInputRef.current?.focus();
                handleInitialChar(props.initialChar);
            }, 0);
        }
    };

    const handleInitialChar = (initialChar) => {
        if (!initialChar) {
            return;
        }

        if (rateInputHasFocus()) {
            setTce({
                ...tce,
                rate: initialChar,
            });
        }
    };

    const rateInputHasFocus = () => {
        return rateInputRef.current === document.activeElement;
    };

    const deliveryInputHasFocus = () => {
        return (
            deliveryInputRef.current.hasFocus() &&
            !deliveryInputRef.current.menuOpened()
        );
    };

    const redeliveryInputHasFocus = () => {
        return (
            redeliveryInputRef.current.hasFocus() &&
            !deliveryInputRef.current.menuOpened()
        );
    };

    const deliverySelected = (
        location: Location,
        _keyCode: string,
        _shift: boolean,
        createLocationPart: (location: Location) => LocationPartInput
    ) => {
        const part = createLocationPart(location);
        const delivery: LocationPartInput = {
            value: part.value,
            partType: part.partType,
            isSTS: part.isSTS,
            name: part.name,
        };
        setTce({ ...tce, delivery: delivery });
        setDeliveryValid(true);
    };

    const redeliverySelected = (
        location: Location,
        _keyCode: string,
        _shift: boolean,
        createLocationPart: (location: Location) => LocationPartInput
    ) => {
        const part = createLocationPart(location);
        const redelivery: LocationPartInput = {
            value: part.value,
            partType: part.partType,
            isSTS: part.isSTS,
            name: part.name,
        };
        setTce({ ...tce, redelivery: redelivery });
        setRedeliveryValid(true);
    };

    const handleInputKeyDown = (e) => {
        switch (e.keyCode) {
            case KEY_BACKSPACE:
                break;
            case KEY_TAB:
                if (props.onTab && props.onTabBack) {
                    e.preventDefault();
                    e.stopPropagation();
                    handleOnAddClick(
                        e,
                        !e.shiftKey ? props.onTab : props.onTabBack
                    );
                }
                break;
            case KEY_LEFT:
            case KEY_RIGHT:
                changeFocusOnKeyDown(containerId, e);
                break;
            case KEY_ENTER:
                if (
                    rateInputHasFocus() ||
                    deliveryInputHasFocus() ||
                    redeliveryInputHasFocus()
                ) {
                    handleOnAddClick(e);
                }
                break;
            case KEY_EQUAL:
                if (e.shiftKey) {
                    handleOnAddSeparator(SEPARATOR_PLUS);
                }
                break;
            case KEY_PLUS:
                handleOnAddSeparator(SEPARATOR_PLUS);
                break;
            case KEY_FORWARD_SLASH:
                handleOnAddSeparator(SEPARATOR_FORWARD_SLASH);
                break;
            default:
        }
    };

    const isPositiveNumber = (value) => {
        const intValue = Number.parseInt(value);
        return _.isInteger(intValue) && intValue > 0;
    };

    const validate = () => {
        let isValid = true;
        if (!isPositiveNumber(tce.rate)) {
            setRateValid(false);
            isValid = false;
        }

        if (
            !_.isNil(tce.ballastBonus) &&
            !_.isEmpty(tce.ballastBonus) &&
            !isPositiveNumber(tce.ballastBonus)
        ) {
            setBallastBonusValid(false);
            isValid = false;
        }
        if (_.isNil(tce.delivery)) {
            setDeliveryValid(false);
            isValid = false;
        }
        if (_.isNil(tce.redelivery)) {
            setRedeliveryValid(false);
            isValid = false;
        }
        return isValid;
    };

    const handleOnAddClick = (e, tabOutAction?: any) => {
        if (tce && validate()) {
            const part = createTcePart(
                Number.parseInt(tce.rate) ?? 0,
                tce.ballastBonus
                    ? Number.parseInt(tce.ballastBonus)
                    : undefined,
                tce.balticIndex ?? '',
                tce.delivery!,
                tce.redelivery!
            );

            props.onAddValue(part, tabOutAction);
            reset();
            focusRate();
            return;
        }

        tabOutAction && tabOutAction();
    };

    const handleOnAddSeparator = (character) => {
        const separatorPart = createSeparatorTcePart(character);
        props.onAddSeparator(separatorPart);
    };

    const parseInt = (value) => {
        return QuantityParserInstance.cleanInteger(value);
    };

    const selectBalticIndexStyles = {
        ...selectStyles,
        input: (provided) => {
            return {
                ...provided,
                paddingTop: '0',
                paddingBottom: '0',
            };
        },
    };

    return (
        <div
            id={containerId}
            style={{ minWidth: 'auto' }}
            onKeyDown={handleInputKeyDown}
            onFocus={props.resetSeparatorValidation}
        >
            <div className="first-inline-item">
                <div style={{ fontWeight: 'bold' }}>Rate*</div>
                <div
                    className="tce-rate-input wrapper"
                    style={{
                        boxShadow: rateValid ? 'inherit' : '0 0 0 1px red',
                        color: rateValid ? 'inherit' : 'red',
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <span style={{ marginLeft: '5px' }}>$</span>
                    <input
                        ref={rateInputRef}
                        type="text"
                        onChange={(e) => {
                            setTce({
                                ...tce,
                                rate: parseInt(e.currentTarget.value),
                            });
                            setRateValid(true);
                        }}
                        value={tce?.rate}
                        placeholder="Amount"
                        style={{
                            color: rateValid ? 'inherit' : 'red',
                        }}
                    />
                    <span style={{ marginRight: '5px' }}>PD</span>
                </div>
                {!rateValid && <p style={{ color: 'red' }}>Rate is invalid</p>}
            </div>
            <div className="inline icon-inside">
                <div style={{ fontWeight: 'bold' }}>BB</div>
                <div
                    className="tce-rate-input wrapper"
                    style={{
                        boxShadow: ballastBonusValid
                            ? 'inherit'
                            : '0 0 0 1px red',
                        color: ballastBonusValid ? 'inherit' : 'red',
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <span style={{ marginLeft: '5px' }}>$</span>
                    <input
                        ref={ballastBonusInputRef}
                        type="text"
                        value={tce?.ballastBonus ?? ''}
                        placeholder="Amount"
                        onChange={(e) => {
                            setTce({
                                ...tce,
                                ballastBonus:
                                    e.currentTarget.value.length === 0
                                        ? undefined
                                        : parseInt(e.currentTarget.value),
                            });
                            setBallastBonusValid(true);
                        }}
                        style={{
                            color: ballastBonusValid ? 'inherit' : 'red',
                        }}
                    />
                </div>

                {!ballastBonusValid && (
                    <p style={{ color: 'red' }}>BB is invalid</p>
                )}
            </div>
            <div className="inline" style={{ maxWidth: '130px' }}>
                <div style={{ fontWeight: 'bold' }}>Baltic Index Vessels</div>
                <Select
                    id="BalticIndexInputA"
                    ref={props.unitInputRef}
                    className="basic-single"
                    classNamePrefix="list"
                    placeholder="Rate index"
                    options={balticOptions as any}
                    menuPlacement="auto"
                    styles={selectBalticIndexStyles}
                    onChange={(option: any) => {
                        setTce({ ...tce, balticIndex: option?.value ?? '' });
                    }}
                    value={
                        balticOptions.find(
                            (x) => x.value === tce.balticIndex
                        ) ?? null
                    }
                    openMenuOnFocus={true}
                />
            </div>
            <div className="inline">
                <div style={{ fontWeight: 'bold' }}>Delivery*</div>
                <LocationSelect
                    ref={deliveryInputRef}
                    context={props.context}
                    field="deliveryLocationParts"
                    onLocationSelected={deliverySelected}
                    shouldSaveInput={true}
                    placeholder="Location"
                    inputClass="tce-input wrapper"
                    onSeparatorEntered={() => {}}
                    onPlaceholderEntered={() => {}}
                    onTab={props.onTab}
                    onTabBack={props.onTabBack}
                    onEnter={props.onEnter}
                    value={tce.delivery?.name ?? ''}
                    onInputClear={() => setTce({ ...tce, delivery: null })}
                />
                {!deliveryValid && (
                    <p style={{ color: 'red' }}>Delivery is required</p>
                )}
            </div>
            <div className="inline">
                <div style={{ fontWeight: 'bold' }}>Redelivery*</div>
                <LocationSelect
                    ref={redeliveryInputRef}
                    context={props.context}
                    field="redeliveryLocationParts"
                    onLocationSelected={redeliverySelected}
                    shouldSaveInput={true}
                    inputClass="tce-input wrapper"
                    onSeparatorEntered={() => {}}
                    onPlaceholderEntered={() => {}}
                    onTab={props.onTab}
                    onTabBack={props.onTabBack}
                    onEnter={props.onEnter}
                    placeholder="Location"
                    value={tce.redelivery?.name ?? ''}
                    onInputClear={() => setTce({ ...tce, redelivery: null })}
                />
                {!redeliveryValid && (
                    <p style={{ color: 'red' }}>Redelivery is required</p>
                )}
            </div>
            <div className="inline" style={{ marginTop: '20px' }}>
                <div className="inline capture-button" title="Capture Rate">
                    <button
                        name="add-rate"
                        className="ui primary button"
                        onClick={handleOnAddClick}
                        disabled={
                            !Boolean(tce?.rate) ||
                            !Boolean(tce?.delivery) ||
                            !Boolean(tce?.redelivery)
                        }
                    >
                        +
                    </button>
                </div>
            </div>
        </div>
    );
});

export default TceInput;
